import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { MdCheckCircle, MdErrorOutline, MdPerson, MdEmail, MdPhone, MdLock, MdRefresh } from 'react-icons/md';
import { FaDumbbell } from 'react-icons/fa';
import { sanitizePhone, isValidPhone, sanitizeEmail, isValidEmail, PHONE_HINT } from '../../utils/inputRules';

// ── Public API instance (no auth / no club header — slug goes in URL) ─────────
const pub = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000' });

// ── Password strength ─────────────────────────────────────────────────────────
const STRENGTH_RULES = [
  { label: '8+ characters', test: (p) => p.length >= 8 },
  { label: '1 uppercase',   test: (p) => /[A-Z]/.test(p) },
  { label: '1 number',      test: (p) => /[0-9]/.test(p) },
  { label: '1 symbol',      test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function PasswordStrength({ password }) {
  const passed = STRENGTH_RULES.filter(r => r.test(password)).length;
  const colors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500'];
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {STRENGTH_RULES.map((_, i) => (
          <div key={i} className={`flex-1 h-1 rounded-full transition-all ${i < passed ? colors[passed - 1] : 'bg-gray-200'}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {STRENGTH_RULES.map(r => (
          <span key={r.label} className={`text-[10px] ${r.test(password) ? 'text-green-600' : 'text-gray-400'}`}>
            {r.test(password) ? '✓' : '○'} {r.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── 6-box OTP input ───────────────────────────────────────────────────────────
function OtpBoxes({ value, onChange }) {
  const inputs = useRef([]);
  const digits  = value.split('');

  const update = (idx, char) => {
    const arr = value.split('');
    arr[idx]  = char;
    onChange(arr.join(''));
    if (char && idx < 5) inputs.current[idx + 1]?.focus();
  };

  const onKey = (e, idx) => {
    if (e.key === 'Backspace') {
      if (!digits[idx] && idx > 0) {
        update(idx - 1, '');
        inputs.current[idx - 1]?.focus();
      } else {
        update(idx, '');
      }
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    inputs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={onPaste}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={el => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={e => update(i, e.target.value.replace(/\D/, '').slice(-1))}
          onKeyDown={e => onKey(e, i)}
          onFocus={e => e.target.select()}
          className="w-11 h-12 text-center text-xl font-bold border-2 rounded-xl focus:outline-none focus:border-primary transition-colors text-secondary bg-nav-hover border-divider"
        />
      ))}
    </div>
  );
}

// ── Countdown timer hook ──────────────────────────────────────────────────────
function useCountdown(seconds) {
  const [left, setLeft] = useState(seconds);
  const reset = useCallback((s) => setLeft(s ?? seconds), [seconds]);
  useEffect(() => {
    if (left <= 0) return;
    const t = setInterval(() => setLeft(p => Math.max(0, p - 1)), 1000);
    return () => clearInterval(t);
  }, [left]);
  const fmt = () => `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  return { left, fmt, reset };
}

// ── Input field helper ────────────────────────────────────────────────────────
function Field({ label, icon: Icon, error, ...props }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />}
        <input
          {...props}
          className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition bg-card text-secondary placeholder-subtle
            ${Icon ? 'pl-9' : ''}
            ${error ? 'border-red-400 focus:ring-red-300' : 'border-divider focus:ring-primary/30'}`}
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────────
function SelfSignup() {
  const { clubSlug } = useParams();
  const [step,     setStep]     = useState(1);
  const [form,     setForm]     = useState({ name:'', email:'', phone:'', gender:'', password:'', confirm:'' });
  const [errors,   setErrors]   = useState({});
  const [otp,      setOtp]      = useState('');
  const [otpError, setOtpError] = useState('');
  const [loading,  setLoading]  = useState(false);

  const otpTimer    = useCountdown(600);  // 10-min OTP expiry display
  const resendTimer = useCountdown(60);   // 60-second resend cooldown

  // Fetch club info for branding
  const { data: clubInfo, isLoading: clubLoading, error: clubError } = useQuery({
    queryKey: ['public-club-info', clubSlug],
    queryFn:  () => pub.get(`/api/public/trial/${clubSlug}/info`).then(r => r.data),
    retry: false,
  });

  const club        = clubInfo?.club;
  const brandColor  = club?.primaryColor || '#E24B4A';
  const clubName    = club?.name || 'the club';

  // ── Form validation ────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.name.trim())          e.name     = 'Full name is required.';
    if (!form.email.trim() || !isValidEmail(form.email)) e.email = 'Valid email required.';
    if (!form.phone.trim())         e.phone    = 'Phone number is required.';
    else if (!isValidPhone(form.phone)) e.phone = PHONE_HINT;
    if (!form.gender)               e.gender   = 'Please select a gender.';
    if (form.password.length < 8)   e.password = 'At least 8 characters.';
    else if (!/[A-Z]/.test(form.password))    e.password = 'Add an uppercase letter.';
    else if (!/[0-9]/.test(form.password))    e.password = 'Add a number.';
    else if (!/[^A-Za-z0-9]/.test(form.password)) e.password = 'Add a special character.';
    if (form.password !== form.confirm) e.confirm = 'Passwords do not match.';
    return e;
  };

  // ── Step 1 submit ──────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      await pub.post('/api/auth/register', {
        name:       form.name.trim(),
        email:      form.email.trim().toLowerCase(),
        password:   form.password,
        phone:      form.phone.trim(),
        gender:     form.gender,
        selfSignup: true,
      }, { headers: { 'X-Club-Slug': clubSlug } });

      setStep(2);
      otpTimer.reset(600);
      resendTimer.reset(60);
    } catch (err) {
      const msg = err.response?.data?.message || 'Registration failed. Please try again.';
      const code = err.response?.data?.code;
      if (code === 'INVITE_ONLY') {
        setErrors({ general: msg });
      } else if (msg.toLowerCase().includes('email')) {
        setErrors({ email: msg });
      } else {
        setErrors({ general: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ─────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendTimer.left > 0) return;
    setLoading(true);
    try {
      await pub.post('/api/auth/send-otp',
        { email: form.email.trim().toLowerCase() },
        { headers: { 'X-Club-Slug': clubSlug } }
      );
      toast.success('New code sent!');
      resendTimer.reset(60);
      otpTimer.reset(600);
      setOtp('');
      setOtpError('');
    } catch (err) {
      const d = err.response?.data;
      if (d?.cooldown) {
        resendTimer.reset(d.secondsLeft);
        setOtpError(`Please wait ${d.secondsLeft}s before resending.`);
      } else {
        toast.error(d?.message || 'Failed to resend code.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 submit ──────────────────────────────────────────────────────────
  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setOtpError('Please enter the 6-digit code.'); return; }
    setLoading(true);
    setOtpError('');
    try {
      await pub.post('/api/auth/verify-otp',
        { email: form.email.trim().toLowerCase(), code: otp },
        { headers: { 'X-Club-Slug': clubSlug } }
      );
      setStep(3);
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  // ── Loading / error states ─────────────────────────────────────────────────
  if (clubLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (clubError || !club) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <MdErrorOutline className="text-5xl text-gray-300 mx-auto mb-3" />
          <h2 className="font-bold text-gray-700 text-lg">Club not found</h2>
          <p className="text-gray-400 text-sm mt-1">
            This link is invalid or the club is no longer active.
          </p>
        </div>
      </div>
    );
  }

  // Invite-only club — show error before any form
  if (errors.general?.includes('not accepting')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <MdErrorOutline className="text-5xl text-red-300 mx-auto mb-3" />
          <h2 className="font-bold text-gray-700 text-lg">Registrations closed</h2>
          <p className="text-gray-500 text-sm mt-1">
            {clubName} is not accepting public registrations. Ask your admin for an invitation link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8">
      {/* Brand header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-lg mb-3"
          style={{ background: brandColor }}>
          <FaDumbbell className="text-white text-2xl" />
        </div>
        <h1 className="font-bold text-gray-800 text-xl">{clubName}</h1>
        <p className="text-gray-500 text-sm mt-0.5">Member registration</p>
      </div>

      {/* Progress bar */}
      <div className="signup-stepper flex items-center gap-2 mb-6">
        {[1,2,3].map(s => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              step > s  ? 'text-white'  :
              step === s ? 'text-white'  : 'bg-gray-200 text-gray-400'
            }`} style={step >= s ? { background: brandColor } : {}}>
              {step > s ? <MdCheckCircle className="text-sm" /> : s}
            </div>
            <span className={`text-xs ${step === s ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
              {s === 1 ? 'Details' : s === 2 ? 'Verify Email' : 'Done'}
            </span>
            {s < 3 && <div className="w-6 h-px bg-gray-200" />}
          </div>
        ))}
      </div>

      {/* Card */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 w-full max-w-md">

        {/* ── Step 1: Details ── */}
        {step === 1 && (
          <form onSubmit={handleRegister} className="space-y-3">
            <div className="mb-4">
              <h2 className="font-bold text-gray-800 text-base">Your details</h2>
              <p className="text-gray-400 text-xs mt-0.5">Fill in your information to create an account.</p>
            </div>

            {errors.general && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-sm text-red-600">
                <MdErrorOutline className="text-lg flex-shrink-0 mt-0.5" />
                {errors.general}
              </div>
            )}

            <Field label="Full Name" icon={MdPerson} placeholder="Your full name"
              value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
              error={errors.name} autoComplete="name" />

            <Field label="Email Address" icon={MdEmail} type="email" placeholder="you@example.com"
              value={form.email} onChange={e => setForm(f => ({...f, email: sanitizeEmail(e.target.value)}))}
              error={errors.email} autoComplete="email" />

            <Field label="Phone Number" icon={MdPhone} type="tel" placeholder="98XXXXXXXX"
              value={form.phone} onChange={e => setForm(f => ({...f, phone: sanitizePhone(e.target.value)}))}
              error={errors.phone} />

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
              <select
                value={form.gender}
                onChange={e => setForm(f => ({...f, gender: e.target.value}))}
                className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white text-gray-800
                  ${errors.gender ? 'border-red-400' : 'border-gray-200'}`}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && <p className="text-xs text-red-500 mt-0.5">{errors.gender}</p>}
            </div>

            <div>
              <Field label="Password" icon={MdLock} type="password" placeholder="Create a strong password"
                value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
                error={errors.password} autoComplete="new-password" />
              {form.password && <PasswordStrength password={form.password} />}
            </div>

            <Field label="Confirm Password" icon={MdLock} type="password" placeholder="Repeat your password"
              value={form.confirm} onChange={e => setForm(f => ({...f, confirm: e.target.value}))}
              error={errors.confirm} autoComplete="new-password" />

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm mt-2 transition-opacity disabled:opacity-60"
              style={{ background: brandColor }}
            >
              {loading ? 'Sending code…' : 'Continue →'}
            </button>

            <p className="text-center text-xs text-gray-400 mt-2">
              Already have an account?{' '}
              <a href="/login" className="font-medium" style={{ color: brandColor }}>Log in</a>
            </p>
          </form>
        )}

        {/* ── Step 2: Verify Email ── */}
        {step === 2 && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="font-bold text-gray-800 text-base">Check your email</h2>
              <p className="text-gray-500 text-xs mt-1">
                We sent a 6-digit code to <strong>{form.email}</strong>
              </p>
            </div>

            <OtpBoxes value={otp} onChange={setOtp} />

            {otpError && (
              <p className="text-center text-xs text-red-500 mt-1">{otpError}</p>
            )}

            {/* Countdown */}
            <div className="text-center text-xs text-gray-400">
              {otpTimer.left > 0 ? (
                <>Code expires in <span className="font-semibold text-gray-600">{otpTimer.fmt()}</span></>
              ) : (
                <span className="text-red-500">Code expired — please resend.</span>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-opacity disabled:opacity-60"
              style={{ background: brandColor }}
            >
              {loading ? 'Verifying…' : 'Verify Code'}
            </button>

            {/* Resend */}
            <div className="text-center">
              <button
                type="button"
                disabled={resendTimer.left > 0 || loading}
                onClick={handleResend}
                className="inline-flex items-center gap-1.5 text-xs font-medium disabled:opacity-40"
                style={resendTimer.left === 0 ? { color: brandColor } : {}}
              >
                <MdRefresh className="text-sm" />
                {resendTimer.left > 0
                  ? `Resend in ${resendTimer.left}s`
                  : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {/* ── Step 3: Done ── */}
        {step === 3 && (
          <div className="text-center py-4 space-y-3">
            <div className="flex items-center justify-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: `${brandColor}20` }}>
                <MdCheckCircle className="text-4xl" style={{ color: brandColor }} />
              </div>
            </div>
            <h2 className="font-bold text-gray-800 text-lg">Application submitted!</h2>
            <p className="text-gray-500 text-sm">
              Your email is verified. The club admin will review your application and send you a confirmation email.
            </p>
            <ul className="text-xs text-gray-400 space-y-1 text-left bg-gray-50 rounded-xl p-3 mt-2">
              <li>✓ Email verified</li>
              <li>○ Waiting for admin approval</li>
              <li>○ You'll receive an email when approved</li>
            </ul>
            <a
              href="/"
              className="block w-full py-2.5 rounded-xl text-white text-sm font-semibold mt-2 text-center"
              style={{ background: brandColor }}
            >
              Back to Home
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default SelfSignup;
